import React, {ReactNode} from 'react';
import {debounce} from 'ts-debounce';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {EntitySummary} from 'constants/UserConstants';
import * as EntityDAO from 'dao/EntityDAO';
import makeCancelable from 'lib/makeCancelable';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {modelFetched} from 'models/ManagedModelsReduxActions';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import {ButtonProps} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import {buildCustomParties, isCustomParty} from 'ui-library/entity-input/EntityInputUtil';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import {listToReadable} from 'util/NameUtil';
import pickDefined from 'util/pickDefined';
import strUtil from 'util/strUtil';
import {ElementKeyboardEvent, InputElementFocusEvent} from 'util/Types';

import EntityInput from './EntityInput';
import {CustomParty} from './EntityInputConstants';
import {EntitySuggestion, EntityWithTextAndSuspended, MinimalEntity} from './EntityInputConstants';
import {LegacyEntityInput} from './LegacyEntityInput';

export const ALL_RESULTS_QUERY = ' ';

// Keep in sync with UserSearchType in Python.
export enum UserSearchType {
  All,
  None,
  NotSuspended,
}

interface BaseEntityInputWithSearchProps {
  placeholder?: string;
  buttonProps?: ButtonProps;
  shouldAllowMultiple?: boolean;
  className?: string;

  // Passed down to TagsInput if rendered using LegacyEntityInput
  allowAdjustInputWidth?: boolean;
  addKeys?: Array<number>;

  // Passed down to ChipSelectInput if rendered using EntityInput
  autoFocus?: boolean;
  entityInputChipSize?: ChipSize;

  // If no entities match, render emptyText in the dropdown.
  emptyText?: ReactNode;

  /**
   * Query for all results when focusing.
   * @default false
   */
  searchAllResultsOnFocus?: boolean;
  /**
   * If no entities match the query AND we've determined that no results will ever be returned by any search,
   * this will override `emptyText` for all searches.
   * Only provide if `searchAllResultsOnFocus` is true.
   *
   * @example
   * emptyText='No matches found'
   * emptyTextForAllResultsSearch='No reviewers available for this stage.'
   */
  emptyTextForAllResultsSearch?: ReactNode;

  // Text to show while still loading results.
  loadingText?: string;
  initialQuery?: string;
  onChangeInput?: (text: string) => void;
  onEnter?: () => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  size: TagsInputSize;

  // A function to filter the entities. Should return a list of entities that should be
  // rendered as options. You are given the user's text input as well as the full list of options
  filterOptions?: (
    text: string,
    options: Array<EntityWithTextAndSuspended>
  ) => Array<EntityWithTextAndSuspended>;

  // A filter function to determine which entities should be disabled.
  disableOptionsFilter?: (text: string, option: EntityWithTextAndSuspended) => boolean;

  disabled?: boolean;
  isReadOnly?: boolean;
}

interface WithCustomSearchProviderProps {
  search?: (query: string) => Promise<SearchResponse>;

  allowEmails?: never;
  allowUnvalidatedEntities?: never;
  userSearchType?: never;
  allowTeams?: never;
  allowOrganizations?: never;
  allowBenchlingApps?: never;
  organizationIds?: never;
}

interface WithoutCustomSearchProviderProps {
  search?: never;

  // If allowEmails is true, an email will show up as a clickable suggestion if the user inputs an email.
  // This also allows emails to be passed to the onSubmit handler as {email}.
  allowEmails?: boolean;
  // If allowUnvalidatedEntities is true, return the unvalidated text as a selected option.
  allowUnvalidatedEntities?: boolean;
  userSearchType?: UserSearchType;
  // If allowTeams is true, teams will be included as entity suggestions.
  allowTeams?: boolean;
  // If allowOrganizations is true, organizations will be included as entity suggestions.
  allowOrganizations?: boolean;
  // If allowBenchlingApps is true, apps will be included as entity suggestions.
  allowBenchlingApps?: boolean;
  organizationIds?: Array<string>;
}

interface WithoutCustomPartyOptionsProps {
  value: Array<MinimalEntity>;
  onChange: (value: Array<EntityWithTextAndSuspended>) => void;
  onHoverOption?: (option: EntityWithTextAndSuspended) => void;
  // customPartyOptions represents additional options in addition to those queried via the API. Usually, you
  // should use this to add party options whose semantical value does not map directly to a specific party, like
  // "No parties", "All parties", or "All parties except x". This prop is not supported by LegacyEntityInput,
  // so this prop is disallowed when enableNewEntityInput is false
  customPartyOptions?: never;
  // Temporary flag to gradually migrate this component from LegacyEntityInput to EntityInput
  enableNewEntityInput?: boolean;
}

interface WithCustomPartyOptionsProps {
  value: Array<MinimalEntity | CustomParty>;
  onChange: (value: Array<EntityWithTextAndSuspended | CustomParty>) => void;
  onHoverOption?: (option: EntityWithTextAndSuspended | CustomParty) => void;
  customPartyOptions?: Array<string>;
  enableNewEntityInput: true;
}

type ConditionalProps = WithCustomPartyOptionsProps | WithoutCustomPartyOptionsProps;
type ConditionalSearchProps = WithCustomSearchProviderProps | WithoutCustomSearchProviderProps;
export type EntityInputWithSearchProps = BaseEntityInputWithSearchProps &
  ConditionalProps &
  ConditionalSearchProps;

interface State {
  suggestions: Array<EntityWithTextAndSuspended>;
  suspendedUserIds: Set<string>;
  text: string;
  isLoading: boolean;
  /**
   * Cached results from querying for all results to determine if any results ever exist.
   * Only used if `props.searchAllResultsOnFocus` is true.
   */
  allPossibleSuggestions?: Array<EntityWithTextAndSuspended>;
}

export interface SearchResponse {
  entitySummaries?: Array<EntityWithTextAndSuspended>;
  suspendedUserIds?: Array<string>;
}

function cacheSearchResults(results?: Array<EntityWithTextAndSuspended>): void {
  const entitySummariesByHandle: {[handle: string]: EntitySummary} = {};
  for (const oneResult of results ?? []) {
    if (oneResult.type === 'user') {
      entitySummariesByHandle[oneResult.handle] = pickDefined(
        oneResult,
        'id',
        'avatar_url',
        'handle',
        'institution',
        'joinedOn',
        'name',
        'type',
        'url_identifier'
      );
    }
  }
  if (Object.keys(entitySummariesByHandle).length > 0) {
    getStore().dispatch(modelFetched(ManagedModelType.ENTITY_SUMMARY_BY_HANDLE, entitySummariesByHandle));
  }
}

export class EntityInputWithSearch extends React.Component<EntityInputWithSearchProps> {
  static defaultProps = {
    allowEmails: false,
    allowUnvalidatedEntities: false,
    userSearchType: UserSearchType.NotSuspended,
    initialQuery: '',
    allowTeams: true,
    allowOrganizations: true,
    allowBenchlingApps: false,
    enableNewEntityInput: false,
  };

  _allowAllResultsQuery(): boolean {
    return this.state.text === '' && Boolean(this.props.searchAllResultsOnFocus);
  }

  _search = (options?: {searchForAllResults?: boolean}): void => {
    const searchForAllResults = Boolean(options?.searchForAllResults);
    const shouldUseAllResultsQuery = searchForAllResults && this._allowAllResultsQuery();
    const query = shouldUseAllResultsQuery ? ALL_RESULTS_QUERY : this.state.text;

    let searchPromise = this.props.search?.(query);
    if (searchPromise == null) {
      const queryParams: {q: string; organizationIds?: string} = {q: query};
      if (this.props.organizationIds) {
        queryParams.organizationIds = this.props.organizationIds.join(',');
      }
      const entityQueryParams = {
        ...queryParams,
        includeTeams: this.props.allowTeams,
        includeOrgs: this.props.allowOrganizations,
        includeBenchlingApps: this.props.allowBenchlingApps,
        userSearchType: this.props.userSearchType,
      };
      searchPromise = Promise.resolve<SearchResponse>(EntityDAO.search(entityQueryParams));
    }
    this.fetchSuggestionsPromise = makeCancelable(searchPromise);
    this.fetchSuggestionsPromise.promise
      .then((response) => {
        if (query === this.state.text || shouldUseAllResultsQuery) {
          this.setState({
            suggestions: response.entitySummaries,
            suspendedUserIds: new Set(response.suspendedUserIds),
            isLoading: false,
            allPossibleSuggestions:
              query === ALL_RESULTS_QUERY ? response.entitySummaries : this.state.allPossibleSuggestions,
          });
          cacheSearchResults(response.entitySummaries);
        }
      })
      .catch((error) => {
        notifications.notifyError(error, {id: 'entityInput'});
        this.setState({isLoading: false});
      });
  };

  fetchSuggestionsPromise: {
    promise: Promise<SearchResponse>;
    cancel: () => void;
  } | null = null;
  _debouncedSearch: (options?: {searchForAllResults?: boolean}) => void = debounce(
    (options?: {searchForAllResults?: boolean}) => {
      this._search(options);
    },
    250
  );

  state: State = {
    suggestions: [],
    suspendedUserIds: new Set(),
    text: this.props.initialQuery ?? '',
    isLoading: false,
  };

  componentWillUnmount(): void {
    if (this.fetchSuggestionsPromise) {
      this.fetchSuggestionsPromise.cancel();
    }
  }

  _getPlaceholder(): string {
    if (this.props.placeholder) {
      return this.props.placeholder;
    }

    // Compute a default based on the allow* parameters and userSearchType, e.g.
    // "Search for a user or team", "Search for a user, email, or team", or "Search for team, or organization"
    const options = [];
    if (this.props.userSearchType !== UserSearchType.None) {
      options.push('user');
    }
    if (this.props.allowEmails) {
      options.push('email');
    }
    if (BenchlingConfig.ENABLE_TEAMS && this.props.allowTeams) {
      options.push('team');
    }
    if (this.props.allowOrganizations) {
      options.push('organization');
    }
    if (this.props.allowBenchlingApps) {
      options.push('app');
    }

    assert(options.length > 0, 'Expected at least one allowed party type');

    return `Search for a ${listToReadable(options, 'or')}`;
  }

  _isSuspended(entity: EntitySuggestion): boolean {
    return this.state.suspendedUserIds.has(entity.id);
  }

  _getSuggestions(): Array<EntitySuggestion | CustomParty> {
    if (this._allowAllResultsQuery() && (this.state.allPossibleSuggestions?.length ?? 0) > 0) {
      return this.state.allPossibleSuggestions!;
    }
    const suggestions: Array<EntitySuggestion | CustomParty> = [
      ...(buildCustomParties(this.props.customPartyOptions) ?? []),
    ];

    if (this.state.suggestions == null) {
      return suggestions;
    }
    if (
      this.props.allowEmails &&
      this.state.suggestions.length === 0 &&
      strUtil.isValidEmail(this.state.text)
    ) {
      suggestions.push({
        id: this.state.text,
        handle: '',
        email: this.state.text,
      });
    } else {
      suggestions.push(
        ...this.state.suggestions.map((suggestion) => ({
          ...suggestion,
          text: suggestion.handle,
          suspended: this._isSuspended(suggestion),
        }))
      );
    }

    return suggestions;
  }

  _filterSuggestions = (
    tag: string,
    suggestions: Array<EntityWithTextAndSuspended>
  ): Array<EntityWithTextAndSuspended> => {
    // Allow the parent component to filter suggestions.
    if (this.props.filterOptions) {
      return this.props.filterOptions(tag, suggestions);
    }
    // Otherwise, assume all options are valid.
    return suggestions;
  };

  _handleChangeInput = (value: string): void => {
    if (this.props.onChangeInput) {
      this.props.onChangeInput(value);
    }
    this.setState({text: value, isLoading: true});
    // Update the suggestions with the new value.
    this._debouncedSearch();
  };

  _handleFocus = (e: InputElementFocusEvent): void => {
    if (this._allowAllResultsQuery()) {
      this.setState({isLoading: true});
      // Update the suggestions with the new value.
      this._debouncedSearch({searchForAllResults: true});
    }
  };

  _handleChange = (entities: Array<CustomParty | EntityWithTextAndSuspended>): void => {
    const filteredEntities = entities.filter((entity) => {
      if (isCustomParty(entity)) {
        return Boolean(this.props.customPartyOptions);
      }

      if (entity.handle) {
        return true;
      }
      if (this.props.allowEmails && entity.email) {
        return true;
      }
      return this.props.allowUnvalidatedEntities && Boolean(entity.text);
    });

    if (this.props.customPartyOptions) {
      this.props.onChange(filteredEntities);
    } else {
      this.props.onChange(filteredEntities as Array<EntityWithTextAndSuspended>);
    }
  };

  getEmptyText(): ReactNode {
    if (this.props.loadingText && this.state.isLoading) {
      return this.props.loadingText;
    }
    if (this.props.searchAllResultsOnFocus && this.props.emptyTextForAllResultsSearch) {
      // Only rely on allPossibleSuggestions if the all results search query actually completed. If it's
      // undefined, it must not have completed.
      if (this.state.allPossibleSuggestions?.length === 0) {
        return this.props.emptyTextForAllResultsSearch;
      }
    }
    return this.props.emptyText ?? '';
  }

  render(): JSX.Element {
    const suggestions = this._getSuggestions();
    if (this.props.enableNewEntityInput) {
      const emptyText = this.getEmptyText();

      const shouldRenderSuggestions = this._allowAllResultsQuery() || this.state.text !== '';

      let options = shouldRenderSuggestions ? suggestions : null;
      if (this.state.isLoading) {
        // Setting options to empty array so that the loading text (`emptyText`) will display.
        options = this.props.loadingText ? [] : null;
      }

      return (
        <EntityInput
          allOptions={options}
          forceDisplayOptionsOnFocus={this.props.searchAllResultsOnFocus}
          autoFocus={this.props.autoFocus}
          value={this.props.value}
          placeholder={this._getPlaceholder()}
          buttonProps={this.props.buttonProps}
          emptyText={emptyText}
          onChange={this._handleChange}
          onChangeInput={this._handleChangeInput}
          onEnter={this.props.onEnter}
          onFocus={this._handleFocus}
          onKeyDown={this.props.onKeyDown}
          filterOptions={this._filterSuggestions}
          disableOptionsFilter={this.props.disableOptionsFilter}
          shouldAllowMultiple={this.props.shouldAllowMultiple}
          query={this.state.text}
          className={this.props.className}
          disabled={this.props.disabled}
          isReadOnly={this.props.isReadOnly}
          chipSize={this.props.entityInputChipSize}
        />
      );
    }

    return (
      <LegacyEntityInput
        allOptions={this._getSuggestions() as Array<EntitySuggestion>}
        value={this.props.value}
        allowAdjustInputWidth={this.props.allowAdjustInputWidth}
        placeholder={this._getPlaceholder()}
        buttonProps={this.props.buttonProps}
        addKeys={this.props.addKeys}
        allowUnvalidatedEntities={this.props.allowUnvalidatedEntities}
        emptyText={this.props.emptyText}
        onChange={this._handleChange}
        onChangeInput={this._handleChangeInput}
        onHoverOption={this.props.onHoverOption}
        onEnter={this.props.onEnter}
        onKeyDown={this.props.onKeyDown}
        filterOptions={this._filterSuggestions}
        shouldAllowMultiple={this.props.shouldAllowMultiple}
        size={this.props.size}
        dataTestAttrs={{component: 'EntityInputWithSearch', element: 'entityInput'}}
        disabled={this.props.disabled}
      />
    );
  }
}
